import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Input } from 'reactstrap';
import { requiredInput as RequiredInput } from '../../../../core/formValidation';
import { IntlType } from '../../../../utils/typing';
import { archiveEmployee, remindPin, unarchiveEmployee } from '../../../employees/actions';
import Employee from '../../../employees/models';
import { Formik } from '../../hooks/useForm';
import { FormSelections } from './hooks/useFormSelections';
import ConfirmDialog from '../ConfirmDialog';
import useEmployees from '../ManageUsers/hooks/useEmployees';
import CashierSiteSelector from './EmployeeForm/CashierSiteSelector';

interface Props {
  intl: IntlType;
  formik: Formik;
  isNew: boolean;
  employee?: Employee;
  formSelections: FormSelections;
}

interface DialogParams {
  isOpen: boolean;
  header: string;
  text: string;
  handleConfirm: () => void;
}

const EmployeeForm = ({ intl, formik, isNew, employee, formSelections }: Props): JSX.Element => {
  const dispatch = useDispatch();

  const { getEmployees, setSearchParams } = useEmployees();

  const [confirmationDialogParams, setConfirmationDialogParams] = useState<DialogParams>({
    isOpen: false,
    header: '',
    text: '',
    handleConfirm: () => {},
  });

  const getTextInput = (fieldName: string, placeholderId: string) => {
    return {
      id: fieldName,
      type: 'text',
      placeholder: intl.formatMessage({ id: placeholderId }),
      ...formik.getFieldProps(fieldName),
    };
  };

  const openArchiveConfirmation = (event: any) => {
    event.preventDefault();

    setConfirmationDialogParams({
      isOpen: true,
      header: intl.formatMessage({ id: 'archiveUser' }),
      text: intl.formatMessage({ id: 'archiveConfirmation' }),
      handleConfirm: handleArchiveUser,
    });
  };

  const openUnarchiveConfirmation = (event: any) => {
    event.preventDefault();

    setConfirmationDialogParams({
      isOpen: true,
      header: intl.formatMessage({ id: 'unarchiveUser' }),
      text: intl.formatMessage({ id: 'unarchiveConfirmation' }),
      handleConfirm: handleUnarchiveUser,
    });
  };

  const handleArchiveUser = () => {
    const body: any = { id: employee?.id };

    const isUserArchived = !!employee?.coreUser?.archivedAt;

    if (!isUserArchived) {
      body.coreUserId = employee?.coreUserId;
    }

    dispatch(archiveEmployee({ data: { body }, closeModal: false }));

    setConfirmationDialogParams({ isOpen: false, header: '', text: '', handleConfirm: () => {} });

    setTimeout(() => {
      setSearchParams({ page: 0 });
      getEmployees();
    }, 2000);
  };

  const handleUnarchiveUser = () => {
    const body: any = { id: employee?.id };

    const isUserArchived = !!employee?.coreUser?.archivedAt;

    if (isUserArchived) {
      body.coreUserId = employee?.coreUserId;
    }

    dispatch(unarchiveEmployee({ data: { body }, closeModal: false }));

    setConfirmationDialogParams({ isOpen: false, header: '', text: '', handleConfirm: () => {} });

    setTimeout(() => {
      setSearchParams({ page: 0 });
      getEmployees();
    }, 2000);
  };

  const handleForgotPin = (event: any) => {
    event.preventDefault();

    dispatch(
      remindPin({
        data: {
          body: { id: employee?.id },
        },
        closeModal: false,
      })
    );
  };

  return (
    <div data-testid="employee-form">
      <div className="form-row half">
        <div className="form-group">
          <label htmlFor="firstName">{intl.formatMessage({ id: 'firstName' })}</label>
          <RequiredInput input={getTextInput('firstName', 'enterName')} />
        </div>
        <div className="form-group">
          <label htmlFor="lastName">{intl.formatMessage({ id: 'lastName' })}</label>
          <RequiredInput input={getTextInput('lastName', 'enterName')} />
        </div>
      </div>
      <div className="form-row half">
        <div className="form-group">
          <label htmlFor="email">{intl.formatMessage({ id: 'email' })}</label>
          <RequiredInput input={getTextInput('email', 'emailExample')} />
          {formik.errors.email && formik.errors.email !== 'Required' && (
            <div className="invalid-feedback d-block" style={{ display: 'block' }}>
              {formik.errors.email}
            </div>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="phoneNumber">{intl.formatMessage({ id: 'phoneNumber' })}</label>
          <RequiredInput input={getTextInput('phoneNumber', 'enterPhone')} />
        </div>
      </div>
      <div className="form-row half">
        <div className="form-group">
          <label htmlFor="position">{intl.formatMessage({ id: 'jobTitle' })}</label>
          <RequiredInput input={getTextInput('position', 'enterTitle')} />
        </div>
        <div className="form-group">
          <label htmlFor="loginCredentials">{intl.formatMessage({ id: 'loginCredentials' })}</label>
          <div className="form-check">
            <label className="form-check-label">
              <Input
                id="loginCredentials"
                {...formik.getFieldProps('loginCredentialsNeeded')}
                type="checkbox"
                className="form-check-input"
                checked={formik.values.loginCredentialsNeeded}
              />{' '}
              {intl.formatMessage({ id: 'managerLoginNeeded' })}
            </label>
          </div>
          <div className="form-check">
            <label className="form-check-label">
              <Input
                id="isCashier"
                {...formik.getFieldProps('isCashier')}
                type="checkbox"
                className="form-check-input"
                checked={formik.values.isCashier}
              />{' '}
              {intl.formatMessage({ id: 'isCashier' })}
            </label>
          </div>
          <div className="form-check">
            <label className="form-check-label">
              <Input
                id="isManager"
                {...formik.getFieldProps('isManager')}
                type="checkbox"
                className="form-check-input"
                checked={formik.values.isManager}
              />{' '}
              {intl.formatMessage({ id: 'isManager' })}
            </label>
          </div>
        </div>
      </div>

      <div className="form-row full">
        <div className="form-group">
          <CashierSiteSelector intl={intl} formSelections={formSelections} formik={formik} />
        </div>
      </div>

      {!isNew && (
        <div className="form-row full">
          <div className="form-group">
            <label htmlFor="actions">{intl.formatMessage({ id: 'actions' })}</label>
            <div className="d-flex flex-row gap-3">
              {!employee?.archivedAt && (
                <Button
                  className="button inverted cancel mr-2"
                  color="secondary"
                  onClick={openArchiveConfirmation}
                >
                  {intl.formatMessage({ id: 'archiveUser' })}
                </Button>
              )}

              {employee?.archivedAt && (
                <Button
                  className="submit-button delete mr-2"
                  color="secondary"
                  onClick={openUnarchiveConfirmation}
                >
                  {intl.formatMessage({ id: 'unarchiveUser' })}
                </Button>
              )}
              {(employee?.isCashier || formik.values.isCashier) && (
                <Button
                  className="button inverted cancel"
                  color="secondary"
                  onClick={handleForgotPin}
                >
                  {intl.formatMessage({ id: 'forgotPin' })}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmationDialogParams.isOpen && (
        <ConfirmDialog
          intl={intl}
          header={confirmationDialogParams.header}
          text={confirmationDialogParams.text}
          handleConfirm={confirmationDialogParams.handleConfirm}
          toggle={() =>
            setConfirmationDialogParams({
              isOpen: false,
              header: '',
              text: '',
              handleConfirm: () => {},
            })
          }
        />
      )}
    </div>
  );
};

export default EmployeeForm;
